import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IFormFieldProps } from '../../../IFormFieldProps';
import { useAppDispatch } from '../../../../../store/hook';
import { incrementCounter, incrementProvided } from '../../../../../store/element/receipt_counter';
import Dropzone from './Dropzone';
import { ITypesType } from '../ITypesType';
import Receipt from '../../../../../domain/Receipt/Receipt';
import FindAllReceiptsUseCase from '../../../../../useCase/Receipt/FindAllReceiptsUseCase';
import LocalStorageReceiptGateway from '../../../../../gateway/Receipt/LocalStorageReceiptGateway';
import FieldErrorMessage from '../../messages/FieldErrorMessage';
import { useFolderId } from '../../../../../infrastructure/hooks';
import ReceiptPreview from './ReceiptPreview';
import IconReceipt from './icon/IconReceipt';
import { EIconReceiptType } from './icon/EIconReceiptType';

interface IProps extends InputHTMLAttributes<HTMLInputElement>, IFormFieldProps {
  classes?: string,
  label?: string,
  help?: string,
  options?: ITypesType[],
  id: string,
  blockId: string,
  register: any
  clearErrors: any
  error: any,
  errorReceiptValidation?: FieldError | FieldError[]
}

const MyUploader = ({ classes, label, help, options, id, blockId, register, clearErrors, error, errorReceiptValidation, ...rest }: IProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch();

  const { folderId } = useFolderId()
  const [receipts, setReceipts] = useState<Receipt[] | null>(null)
  const [isOpen, setIsOpen] = useState(false);
  const [isProvided, setIsProvided] = useState(false);

  useEffect(() => {
    const allRreceipts = new FindAllReceiptsUseCase(new LocalStorageReceiptGateway(folderId)).execute()
    setReceipts(allRreceipts)

    const receiptElement = allRreceipts?.filter((receipt) => {
      return receipt.elementId === id
    })

    if (receiptElement && receiptElement.length > 0) {
      setIsProvided(true)
      if (rest.required) {
        dispatch(incrementProvided())
      }
    } else {
      setIsProvided(false)
    }

    if (rest.required) {
      dispatch(incrementCounter())
    }
  }, [])

  const updateReceipts = (newReceipts: Receipt[]) => {
    setReceipts(newReceipts)

    const receiptElement = newReceipts?.filter((receipt) => {
      return receipt.elementId === id
    })

    if (receiptElement && receiptElement.length > 0) {
      setIsProvided(true)
    } else {
      setIsProvided(false)
    }
  }

  const toggleCard = () => {
    setIsOpen(!isOpen);
  };

  const fieldRef = React.useRef<HTMLInputElement>(null);
  if (error && fieldRef.current) {
    fieldRef.current.scrollIntoView();
  }

  return (
    <>
      {error && !errorReceiptValidation && <FieldErrorMessage type={error.type} message={error.message} />}

      <div className={`doc-card ${isProvided ? 'done' : rest.required ? 'req' : 'opt'} ${classes}`}>
        <div className={`doc-header  ${rest.readOnly && "readonly-element"}`}>
          <div className={`doc-icon ${rest.required ? 'req' : 'opt'}`}>
            <IconReceipt type={options?.[0]?.receipt_type} color={rest.required ? 'var(--primary)' : '#4B4B4D'} />
          </div>

          <div className='doc-main-content'>
            <div className='doc-info'>
              <div className='doc-name'>{label}</div>
              <div className='doc-sub'>{help}</div>
            </div>

            <div className="uploader-reveal" onClick={(e) => e.stopPropagation()}>
              <Dropzone
                key={options?.[0]?.id}
                blockId={blockId}
                id={id}
                typeId={options?.[0]?.id ?? ''}
                receiptsDefault={receipts}
                register={register}
                clearErrors={clearErrors}
                error={error}
                errorReceiptValidation={errorReceiptValidation}
                isRequired={rest.required}
                updateReceipts={updateReceipts}
              />
            </div>
          </div>

          <div className='card-meta'>
            <span className={isProvided ? 'badge-done' : rest.required ? 'badge-req' : 'badge-opt'}>
              {isProvided ?
                t('common.provided')
                :
                t(`common.${rest.required ? 'required' : 'optional'}`)
              }
            </span>
          </div>
          <div
            className={`chevron-icon ${isOpen && isProvided ? 'rotated' : ''}`}
            onClick={toggleCard}
            title={isProvided ? '' : t('common.not-provided')}
          >
            <IconReceipt type={EIconReceiptType.ARROW_DOWN} color={isProvided ? 'var(--ink-1)' : 'var(--grey-6)'} />
          </div>
        </div>
        <div style={{ display: isOpen ? 'block' : 'none' }}>
          <ReceiptPreview
            key={options?.[0]?.id}
            id={id}
            receipts={receipts}
            readOnly={rest.readOnly}
            errorReceiptValidation={errorReceiptValidation}
            isRequired={rest.required}
            updateReceipts={updateReceipts}
          />

        </div>

        <span ref={fieldRef} />

        {error && (!errorReceiptValidation || (Array.isArray(errorReceiptValidation) && errorReceiptValidation.length === 0)) && (
          <FieldErrorMessage type={error.type} message={error.message} classe='u-mbb u-pxs' />
        )}
      </div>
    </>
  )
}

export default MyUploader;
